<!-- app-subdomain-creators/src/Components/NewsFeedMediaPostViewer.vue -->

<template>
  <Teleport to="body">
    <div
      v-if="show && mediaItems.length"
      class="fixed inset-0 z-[999] bg-black/90 backdrop-blur-sm flex"
      @click="onBackdropClick"
    >
      <!-- LEFT: media viewer -->
      <div class="flex-1 min-w-0 flex items-center justify-center relative overflow-hidden">
        <button
          type="button"
          class="absolute top-4 left-4 z-[1100] h-9 w-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white pointer-events-auto"
          @click.stop.prevent="emitClose"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>

        <div class="absolute top-3 left-14 px-4 py-3 text-sm text-zinc-200">
          Viewing {{ currentIndex + 1 }} of {{ mediaItems.length }}
        </div>

        <div v-if="currentItem?.type === 'image'" class="absolute top-3 right-4 flex gap-2">
          <button
            type="button"
            class="h-9 w-9 rounded-full flex items-center justify-center bg-zinc-200/90 transition-colors"
            :class="zoomLevel === maxZoomSteps ? 'opacity-40 cursor-default' : 'hover:bg-zinc-100 cursor-pointer'"
            :disabled="zoomLevel === maxZoomSteps"
            @click.stop="zoomIn"
          >
            <MagnifyingGlassPlusIcon class="h-6 w-6" :class="zoomLevel === maxZoomSteps ? 'text-zinc-400' : 'text-zinc-900'" />
          </button>

          <button
            type="button"
            class="h-9 w-9 rounded-full flex items-center justify-center bg-zinc-200/90 transition-colors"
            :class="zoomLevel === 0 ? 'opacity-40 cursor-default' : 'hover:bg-zinc-100 cursor-pointer'"
            :disabled="zoomLevel === 0"
            @click.stop="zoomOut"
          >
            <MagnifyingGlassMinusIcon class="h-6 w-6" :class="zoomLevel === 0 ? 'text-zinc-400' : 'text-zinc-900'" />
          </button>
        </div>

        <button
          v-if="hasPrev"
          type="button"
          class="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-zinc-400 hover:bg-zinc-200 flex items-center justify-center text-zinc-800"
          @click.stop="goPrev"
        >
          <ChevronLeftIcon class="h-7 w-7" />
        </button>

        <button
          v-if="hasNext"
          type="button"
          class="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-zinc-400 hover:bg-zinc-200 flex items-center justify-center text-zinc-800"
          @click.stop="goNext"
        >
          <ChevronRightIcon class="h-7 w-7" />
        </button>

        <template v-if="currentItem">
          <img
            v-if="currentItem.type === 'image'"
            :src="currentItem.src"
            alt=""
            class="max-w-[80%] max-h-[80%] object-contain rounded-lg transition-transform duration-300 origin-center -z-10"
            :style="mediaTransformStyle"
          />

          <div
            v-else
            class="max-w-[85%] w-full h-[70vh] rounded-lg bg-black overflow-hidden"
          >
            <PlatformaVideoPlayer
              v-if="currentItem.fileId"
              :file-id="String(currentItem.fileId)"
              :playlist-url="buildNewsfeedPlaylistUrl(currentItem.fileId)"
            />
            <video
              v-else
              controls
              autoplay
              playsinline
              class="w-full h-full object-contain"
            >
              <source :src="currentItem.src" />
            </video>
          </div>
        </template>
      </div>

      <!-- RIGHT: interactive post sidebar -->
      <div class="relative z-[1200] w-[440px] max-w-full bg-white flex flex-col border-l border-zinc-200 h-full" @click.stop>
        <div class="flex items-center justify-between px-4 py-2 border-b border-zinc-200">
          <span class="text-xs text-zinc-700">
            {{ currentItem?.type === 'video' ? 'This video is from a post.' : 'This photo is from a post.' }}
          </span>
          <button type="button" class="text-xs font-semibold text-[#2D8CFF] hover:underline" @click="openPostFromSidebar">
            View post
          </button>
        </div>

        <div class="flex-1 min-h-0 overflow-visible">
          <component
            :is="slugComponent"
            v-if="slugComponent"
            :is-open="true"
            :inline-sidebar="true"
            :post="post"
            :posting-as-name="post.currentUserName || 'You'"
            :posting-as-avatar-url="commenterAvatarSrc || null"
            :feature-instance-id="resolvedFeatureInstanceId"
            @open-full-post="forwardOpenFullPost"
          />
          <div v-else class="p-4 text-sm text-zinc-500">
            Unable to load post details in sidebar.
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref, watch } from 'vue';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
} from '@heroicons/vue/24/outline';
import PlatformaVideoPlayer from '@/Components/PlatformaVideoPlayer.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  mediaItems: {
    type: Array,
    default: () => [],
  },
  initialIndex: {
    type: Number,
    default: 0,
  },
  featureInstanceId: {
    type: [Number, String],
    default: null,
  },
  post: {
    type: Object,
    required: true,
  },
  commenterAvatarSrc: {
    type: String,
    default: '',
  },
  authorAvatarSrc: {
    type: String,
    default: '',
  },
  displayTime: {
    type: String,
    default: '',
  },
  slugComponent: {
    type: [Object, Function, String],
    default: null,
  },
});

const emit = defineEmits(['close', 'open-full-post']);
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL || '';

const currentIndex = ref(0);
const zoomLevel = ref(0);
const maxZoomSteps = 4;
const zoomStep = 0.25;
const baseScale = 1;

const zoomScale = computed(() => baseScale + zoomLevel.value * zoomStep);

const mediaTransformStyle = computed(() => ({
  transform: `scale(${zoomScale.value})`,
  transition: 'transform 150ms ease-out',
}));

watch(
  () => [props.show, props.initialIndex, props.mediaItems.length],
  () => {
    if (!props.show) return;
    const max = props.mediaItems.length > 0 ? props.mediaItems.length - 1 : 0;
    let idx = props.initialIndex || 0;
    if (idx < 0) idx = 0;
    if (idx > max) idx = max;
    currentIndex.value = idx;
    zoomLevel.value = 0;
  },
  { immediate: true }
);

watch(
  () => currentIndex.value,
  () => {
    zoomLevel.value = 0;
  }
);

const currentItem = computed(() => props.mediaItems[currentIndex.value] || null);
const hasPrev = computed(() => currentIndex.value > 0);
const hasNext = computed(() => currentIndex.value < props.mediaItems.length - 1);

const goPrev = () => {
  if (hasPrev.value) currentIndex.value -= 1;
};

const goNext = () => {
  if (hasNext.value) currentIndex.value += 1;
};

const zoomIn = () => {
  if (zoomLevel.value < maxZoomSteps) zoomLevel.value += 1;
};

const zoomOut = () => {
  if (zoomLevel.value > 0) zoomLevel.value -= 1;
};

const emitClose = () => {
  emit('close');
};

const onBackdropClick = (event) => {
  if (event.target === event.currentTarget) {
    emitClose();
  }
};

const forwardOpenFullPost = (payload) => {
  const resolvedPayload =
    payload || { postId: props.post?.id, post: props.post, source: 'media-viewer-sidebar' };

  // Ensure viewer closes before opening the full post slug so it doesn't render behind the overlay.
  emitClose();

  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => emit('open-full-post', resolvedPayload));
    return;
  }

  emit('open-full-post', resolvedPayload);
};

const openPostFromSidebar = () => {
  forwardOpenFullPost({ postId: props.post?.id, post: props.post, source: 'media-viewer-view-post' });
};

const resolvedFeatureInstanceId = computed(() => {
  const raw =
    props.featureInstanceId ??
    props.post?.featureInstanceId ??
    props.post?.feature_instance_id ??
    props.post?.instanceId ??
    null;
  const normalized = String(raw ?? '').trim();
  return normalized || null;
});

function buildNewsfeedPlaylistUrl(fileId) {
  const normalizedFileId = String(fileId || '').trim();
  if (!normalizedFileId) return '';

  const featureId = resolvedFeatureInstanceId.value;
  if (!featureId) {
    return `${apiProxyUrl}/api/stream-hls/playlist/${encodeURIComponent(normalizedFileId)}`;
  }
  return `${apiProxyUrl}/api/creator-newsfeed/stream-hls/playlist/${encodeURIComponent(normalizedFileId)}?featureInstanceId=${encodeURIComponent(featureId)}`;
}
</script>
